import React from 'react';
import './SummaryDashboard.css';
import MetricCard from './MetricCard';
import AnimatedGraphicsChart from './AnimatedGraphicsChart';
import MagicBento from './MagicBento';
import AnimatedInsightsChart from './AnimatedInsightsChart';
import InsightsLineChart from './InsightsLineChart';
import { calculateDashboardMetrics, formatDuration } from '../utils/dataProcessor';

const SummaryDashboard = ({ data, allData, year, tournaments }) => {
  // SummaryDashboard render
  
  if (!data || data.length === 0) {
    // No data available
    return <div className="no-data">No data available</div>;
  }

  try {
    // Summary uses filtered 2025 data for metrics
    // Using 2025 data for calculations

    // Calculate all metrics using the same logic as Streamlit dashboard
    const metrics = calculateDashboardMetrics(data, tournaments);

    // Metrics calculated

    return (
      <div className="summary-dashboard">
        {/* Key Metrics */}
        <div className="metrics-grid">
          <MetricCard 
            title="Total Tournaments" 
            value={metrics.totalTournaments} 
          />
          <MetricCard 
            title="GFX On-Screen Time (Hours)" 
            value={metrics.totalDuration / 3600} 
          />
          <MetricCard 
            title="Unique Sponsors" 
            value={metrics.totalSponsors} 
          />
          <MetricCard 
            title="Unique Graphics" 
            value={metrics.totalGraphics} 
          />
        </div>

        {/* Top 10 Graphics Chart */}
        <div className="chart-section-wrapper">
          <div className="chart-section">
            <h3 style={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M13 15.9C15.2822 15.4367 17 13.419 17 11V4H7V11C7 13.419 8.71776 15.4367 11 15.9V18H9V20H15V18H13V15.9ZM9 6H15V11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11V6Z"
                  fill="currentColor"
                />
                <path d="M18 6H20V11H18V6Z" fill="currentColor" />
                <path d="M6 6H4V11H6V6Z" fill="currentColor" />
              </svg>
              Top 10 Graphics by On-Screen Duration
            </h3>
            <AnimatedGraphicsChart 
              data={metrics.top10Graphics} 
              height={648}
            />
          </div>
        </div>

        {/* Magic Bento Component */}
        <div className="magic-bento-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2"/>
            </svg>
            Sponsor Exposure Review
          </h3>
          <MagicBento 
            tournamentLogoHours={formatDuration(metrics.tournamentLogoDuration)}
            insightsHours={formatDuration(metrics.insightsDuration)}
            pifHours={formatDuration(metrics.pifDuration)}
            lexusHours={formatDuration(metrics.lexusDuration)}
            rolexHours={formatDuration(metrics.rolexDuration)}
          />
        </div>

        {/* Insights Breakdown */}
        <div className="insights-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Insights Breakdown
          </h3>
          
          <div className="insights-metric">
            <MetricCard 
              title="Total Insights for Season" 
              value={metrics.totalInsights} 
            />
          </div>

          <div className="insights-charts">
            <div className="insights-chart">
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.5 5.25C3.5 4.83579 3.16421 4.5 2.75 4.5C2.33579 4.5 2 4.83579 2 5.25V17.25C2 18.4926 3.00736 19.5 4.25 19.5H21.25C21.6642 19.5 22 19.1642 22 18.75C22 18.3358 21.6642 18 21.25 18H4.25C3.83579 18 3.5 17.6642 3.5 17.25V5.25Z" stroke="currentColor" stroke-width="1.5"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 10.2773C5.89543 10.2773 5 11.1728 5 12.2773V15.7501C5 16.1643 5.33579 16.5001 5.75 16.5001H8.25C8.66421 16.5001 9 16.1643 9 15.7501V12.2773C9 11.1728 8.10457 10.2773 7 10.2773ZM6.5 12.2773C6.5 12.0012 6.72386 11.7773 7 11.7773C7.27614 11.7773 7.5 12.0012 7.5 12.2773V15.0001H6.5V12.2773Z" stroke="currentColor" stroke-width="1.5"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 6.5C10.5 5.39543 11.3954 4.5 12.5 4.5C13.6046 4.5 14.5 5.39543 14.5 6.5V15.7501C14.5 16.1643 14.1642 16.5001 13.75 16.5001H11.25C10.8358 16.5001 10.5 16.1643 10.5 15.7501V6.5ZM12.5 6C12.2239 6 12 6.22386 12 6.5V15.0001H13V6.5C13 6.22386 12.7761 6 12.5 6Z" stroke="currentColor" stroke-width="1.5"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M18 8.05859C16.8954 8.05859 16 8.95402 16 10.0586V15.7501C16 16.1643 16.3358 16.5001 16.75 16.5001H19.25C19.6642 16.5001 20 16.1643 20 15.7501V10.0586C20 8.95402 19.1046 8.05859 18 8.05859ZM17.5 10.0586C17.5 9.78245 17.7239 9.55859 18 9.55859C18.2761 9.55859 18.5 9.78245 18.5 10.0586V15.0001H17.5V10.0586Z" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                Insight Type
              </h4>
              <AnimatedInsightsChart data={metrics.insightTypes} height={300} />
            </div>
            
            <div className="insights-chart">
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M13 15.9C15.2822 15.4367 17 13.419 17 11V4H7V11C7 13.419 8.71776 15.4367 11 15.9V18H9V20H15V18H13V15.9ZM9 6H15V11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11V6Z"
                    fill="currentColor"
                  />
                  <path d="M18 6H20V11H18V6Z" fill="currentColor" />
                  <path d="M6 6H4V11H6V6Z" fill="currentColor" />
                </svg>
                Top 5 Events
              </h4>
              <AnimatedInsightsChart data={metrics.top5Events} height={300} />
            </div>
          </div>
        </div>

        {/* Insights Line Chart */}
            <div className="insights-line-chart-section">
                <InsightsLineChart data={allData || data} year={year} tournaments={tournaments} />
        </div>
      </div>
    );
  } catch (error) {
    console.error('Error in SummaryDashboard:', error);
    return (
      <div className="error-container">
        <h2>Error Loading Dashboard</h2>
        <p>An error occurred while loading the dashboard: {error.message}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }
};

export default SummaryDashboard; 